import { customEvent } from "goi_common";
import {
  ArrowRightIcon,
  CircleCheckIcon,
  TrendingDownIcon,
} from "@/components/DirectionsIcons";
import type {
  MubinsoCostBreakdown,
  MubinsoTemplate,
} from "@/utils/buildMubinsoPageProps";
import { MubinsoPhoneCta } from "./MubinsoLinks";

/** 고이 무빈소장 상조에 포함되는 것. goi_common 의 무빈소장2026 구성을 요약한 것 */
const SERVICE_ITEMS = [
  "관, 수의 및 필수 장례 용품 일체 포함",
  "고인 전용 이송 차량 및 리무진 지원",
  "전문 장례지도사 1:1 맞춤 밀착 케어",
  "사용하지 않은 품목은 100% 공제",
];

interface MubinsoCostProps {
  parlorId: number;
  companyName: string;
  cost: MubinsoCostBreakdown;
  ctaHref: string;
  serviceHref: string;
  variant?: MubinsoTemplate;
}

export const MubinsoCost = ({
  parlorId,
  companyName,
  cost,
  ctaHref,
  serviceHref,
  variant = "A",
}: MubinsoCostProps) => {
  const isB = variant === "B";
  return (
    <section className="mb-cost" aria-labelledby="mb-cost-title">
      <div className="mb-cost__head">
        <h2 id="mb-cost-title" className="mb-cost__title">
          {isB ? `${companyName} 무빈소 총비용` : "무빈소 시 장례 예상 총 비용"}
        </h2>
        <p className="mb-cost__price">{cost.totalLabel}</p>
        {cost.savingsLabel && (
          <p className="mb-cost__savings">
            <TrendingDownIcon />
            {`가족장 대비 장례식장 비용 약 ${cost.savingsLabel} 절감`}
          </p>
        )}
      </div>

      <div className="mb-split">
        <div className="mb-split__item">
          <h3 className="mb-split__label">장례식장 비용</h3>
          <p className="mb-split__amount">{cost.parlorLabel}</p>
          <p className="mb-split__desc">
            {isB
              ? `안치료, 수시비, 입관실, 쓰레기 수거료처럼 ${companyName}에 직접 내는 금액입니다. 빈소 사용료와 접객 음식비는 무빈소라 빠집니다.`
              : `${companyName}에 내는 안치실 사용료 등 시설 이용료입니다. 법령상 사망 후 24시간 이내에는 화장할 수 없어 안치는 반드시 필요합니다. 빈소를 차리지 않으므로 빈소 사용료와 조문객 음식 비용은 들지 않습니다.`}
          </p>
          {/* B 는 바로 위 요금표 섹션에 출처 고지가 있어 반복하지 않는다 */}
          {!isB && (
            <p className="mb-split__source">
              e하늘 장사정보시스템 공공 데이터 기반, 장례식장마다 다릅니다.
            </p>
          )}
        </div>

        <div className="mb-split__item mb-split__item--accent">
          <h3 className="mb-split__label">고이 무빈소장 상조</h3>
          <p className="mb-split__amount">
            {cost.serviceLabel}
            <span className="mb-split__list-price">{`정상가 ${cost.serviceListLabel}`}</span>
          </p>
          <p className="mb-split__desc">
            {isB
              ? "지역과 장례식장에 관계없이 동일합니다."
              : "전국 어디서나 같은 가격입니다."}
          </p>
          <ul className="mb-cost__includes">
            {SERVICE_ITEMS.map((item) => (
              <li key={item}>
                <CircleCheckIcon size={24} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <a
            className="mb-split__link"
            href={serviceHref}
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_mubinso",
                  trigger_and_target: "btn_to_service",
                  properties: { parlor_id: parlorId },
                },
              })
            }
          >
            고이 무빈소장 구성 자세히 보기
            <ArrowRightIcon size={14} />
          </a>
        </div>
      </div>

      {isB ? (
        <div className="mb-cost__caution">
          <p>
            <strong className="mb-cost__caution-strong">
              * 안치실 사정에 따라 무빈소 진행이 어려울 수 있어 상담에서 먼저
              확인해 드립니다.
            </strong>
          </p>
          <p>
            * 금액은 고이장례연구소 추산치로, 실제 정산 금액과 다를 수 있습니다.
          </p>
        </div>
      ) : (
        <div className="mb-cost__caution">
          <p>
            <strong>* </strong>
            <strong className="mb-cost__caution-strong">
              장례식장 상황에 따라 무빈소 장례가 불가능할 수 있습니다.
            </strong>
          </p>
          <p>
            {
              "* 장례식장 비용은 고이장례연구소에서 자체적으로 추산한 예상 비용입니다. 실제 비용은 장례 시점과 선택하는 항목에 따라 달라질 수 있습니다."
            }
          </p>
        </div>
      )}

      <div className="mb-cta-group">
        <a
          className="mb-cta"
          href={ctaHref}
          onClick={() =>
            customEvent({
              newGtm: {
                path: "prlr_mubinso",
                trigger_and_target: "btn_cta_cost",
                properties: { parlor_id: parlorId },
              },
            })
          }
        >
          {isB
            ? "무빈소 장례 절차 및 비용 자세히 알아보기"
            : "무빈소 장례 자세히 알아보기"}
          <ArrowRightIcon size={16} />
        </a>
        <MubinsoPhoneCta parlorId={parlorId} slot="cost" />
      </div>
    </section>
  );
};
